import { useState } from "react";
import {
  useNavigate,
  useSearchParams,
  Link as RouterLink,
  Navigate,
} from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Stack,
  Chip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useAuth } from "@/contexts/AuthContext";
import { minHeightDvh } from "@/utils/viewport";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // PDF-11: signup-time ToS + Privacy acceptance. Required; submit blocked
  // until ticked. The server records the canonical accepted_at timestamp.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const selectedPlan = searchParams.get("plan");
  const planLabel = selectedPlan
    ? selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)
    : null;

  // Plan selection is required — redirect to pricing if none chosen
  if (!selectedPlan) {
    return <Navigate to="/pricing" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (!termsAccepted) {
      setError(
        "You must accept the Terms of Service and Privacy Policy to continue.",
      );
      return;
    }

    setLoading(true);

    try {
      await register({
        email,
        password,
        plan: selectedPlan,
        termsAccepted: true,
      });
      navigate("/dashboard/billing?welcome=1");
    } catch (err: unknown) {
      if (err && typeof err === "object" && "response" in err) {
        const axiosErr = err as { response?: { data?: { error?: string } } };
        setError(axiosErr.response?.data?.error ?? "Registration failed");
      } else {
        setError("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        ...minHeightDvh,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: (theme) =>
          theme.palette.mode === "light"
            ? "linear-gradient(160deg, #1d4ed8 0%, #6366f1 40%, #a78bfa 100%)"
            : "linear-gradient(160deg, #0c1a3d 0%, #0f0b2e 40%, #1e0a4a 100%)",
        px: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 440, borderRadius: 4 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Gradient logo icon */}
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              background: "linear-gradient(135deg, #1d4ed8, #a78bfa)",
              mx: "auto",
              mb: 1.5,
            }}
          />

          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            align="center"
            fontWeight={700}
          >
            PDFGlyph
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 1 }}
          >
            Create your account
          </Typography>

          {planLabel && planLabel !== "Free" && (
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Chip
                label={`${planLabel} plan — 7-day free trial`}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoComplete="email"
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                helperText="At least 8 characters"
              />
              <TextField
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    inputProps={{
                      "aria-required": true,
                      "aria-label":
                        "Accept Terms of Service and Privacy Policy",
                    }}
                    required
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    I have read and accept the{" "}
                    <Link
                      component={RouterLink}
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      component={RouterLink}
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </Typography>
                }
                sx={{
                  alignItems: "flex-start",
                  ml: 0,
                  mr: 0,
                  "& .MuiFormControlLabel-label": { mt: 0.5 },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading || !termsAccepted}
                sx={{
                  borderRadius: 24,
                  background: "linear-gradient(135deg, #1d4ed8, #a78bfa)",
                  color: "white",
                  "&:hover": {
                    background: "linear-gradient(135deg, #1e40af, #7c3aed)",
                  },
                }}
              >
                {loading
                  ? "Creating account..."
                  : planLabel && planLabel !== "Free"
                    ? `Start free trial`
                    : "Create Account"}
              </Button>
            </Stack>
          </form>

          <Typography variant="body2" align="center" sx={{ mt: 3 }}>
            Already have an account?{" "}
            <Link component={RouterLink} to="/login">
              Sign in
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
